import type React from "react";
import {observer} from "mobx-react-lite";
import {Box, Skeleton, Stack, Typography} from "@mui/material";
import type {SupplierGoodsAndServicesEntry} from "~/pages/supplier-360/types/Supplier360Types";
import {Tag} from "~/components/tag/Tag";
import {SourceLogo} from "~/components/source-logo/SourceLogo";
import {sourceLogoFromName} from "~/components/source-logo/sourceLogos";
import {useSourceLogoAllowed} from "~/pages/supplier-360/hooks/useSourceLogoAllowed";

type GoodsAndServicesFieldProps = {
    /**
     * `SupplierEntity.goods_and_services` (§2.1, added 2026-10-05) — one entry per contributing
     * source, each holding **several** strings, manual-first then most-recently-sourced.
     */
    entries: SupplierGoodsAndServicesEntry[];
    /** Only the Storybook cases need this — the entity is already loaded wherever this renders. */
    isLoading?: boolean;
};

export const GoodsAndServicesField: React.FC<GoodsAndServicesFieldProps> = ({entries, isLoading = false}) => {

    return (
        <Box>
            <Typography variant="caption" color="grey.700" sx={{display: 'block', mb: 1}}>
                Goods &amp; services
            </Typography>
            {/* The design separates the source rows by more than it separates a row's own label. */}
            <Stack  gap={1.5} sx={{'& > * + *': {pt: 1.5}}}>
                {isLoading
                    ? <Skeleton variant="text" width="100%" height={40}/>
                    : entries.length > 0
                        ? entries.map(entry => (
                            <SourceRow key={`${entry.source.id}-${entry.is_manual}`} label={entry.source.name}>
                                <Stack direction="row" gap={0.5}>
                                    {entry.value.map(fact => (
                                        <Tag key={fact} color="default" variant="outlined" label={fact} sx={{maxWidth: 'max-content'}}/>
                                    ))}
                                </Stack>
                            </SourceRow>
                        ))
                        : <EmptyState/>}
            </Stack>
        </Box>
    );
};

const SourceRow: React.FC<{label: string; children: React.ReactNode}> = observer(({label, children}) => {
    const isLogoAllowed = useSourceLogoAllowed();
    // Known providers show their logo, as in the design; manual edits and others keep the name.
    // A provider whose logo the customer config doesn't allow shows no label at all.
    const logo = sourceLogoFromName(label);
    return (
        <Stack gap={0.75}>
            {logo
                ? isLogoAllowed(logo) && <SourceLogo source={logo}/>
                : (
                    <Typography
                        variant="caption"
                        color="grey.700"
                        sx={{fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em'}}
                    >
                        {label}
                    </Typography>
                )}
            {children}
        </Stack>
    );
});

const EmptyState: React.FC = () => (
    <Typography variant="body2" color="text.disabled">—</Typography>
);
